import { z } from 'zod';
import { IntentSchema, PrimaryMotiveSchema } from './enums.js';
import { IntentLevelSchema, SeveritySchema, type IntentLevel } from './evaluation.js';
import { ObjectionTypeSchema } from './projects.js';
import { DimensionEvaluationSchema } from './rubric.js';

// Conversation Intelligence: first-class, versioned, evidence-linked semantic state of a conversation (episode).
// One structured AI call produces it; it is persisted as findings with stable keys, and criteria/scores/alerts
// are derived from it (never the other way around). AI-inferred appointments are NEVER verified.

export const INTELLIGENCE_SCHEMA_VERSION = 'conversation-intelligence-v1';

// ───────── Catalogs ─────────

/** §11/§21 signals. The first group indicates high intent. */
export const HIGH_INTENT_SIGNALS = [
  'asks_price',
  'asks_availability',
  'asks_payment_terms',
  'asks_delivery_date',
  'asks_location',
  'wants_visit',
  'compares_units',
  'asks_discount',
  'shares_budget',
  'mentions_purchase_date',
  'asks_how_to_reserve',
] as const;
export const OTHER_INTENT_SIGNALS = ['requests_information', 'asks_questions', 'explores_alternatives', 'minimal_responses', 'no_timing', 'only_downloaded_information'] as const;
export const INTENT_SIGNAL_TYPES = [...HIGH_INTENT_SIGNALS, ...OTHER_INTENT_SIGNALS] as const;
export const IntentSignalTypeSchema = z.enum(INTENT_SIGNAL_TYPES);
export type IntentSignalType = z.infer<typeof IntentSignalTypeSchema>;
const HIGH_SET = new Set<string>(HIGH_INTENT_SIGNALS);
export const isHighIntentSignal = (t: IntentSignalType): boolean => HIGH_SET.has(t);

/** §12 qualification slots. */
export const QUALIFICATION_SLOTS = ['purpose', 'budget', 'payment_method', 'zone', 'property_type', 'bedrooms', 'timing', 'motive', 'comparing', 'main_objection'] as const;
export const QualificationSlotSchema = z.enum(QUALIFICATION_SLOTS);
export type QualificationSlot = z.infer<typeof QualificationSlotSchema>;

/** §14 question topics (critical topics weigh more when ignored). */
export const QUESTION_TOPICS = ['price', 'availability', 'location', 'financing', 'delivery', 'maintenance', 'amenities', 'process', 'other'] as const;
export const QuestionTopicSchema = z.enum(QUESTION_TOPICS);
export type QuestionTopic = z.infer<typeof QuestionTopicSchema>;
export const CRITICAL_QUESTION_TOPICS: readonly QuestionTopic[] = ['price', 'availability', 'location', 'financing', 'delivery'];

export const QUESTION_HANDLINGS = ['answered', 'partial', 'evaded', 'ignored', 'changed_topic', 'pending'] as const;
export const QuestionHandlingSchema = z.enum(QUESTION_HANDLINGS);
export type QuestionHandling = z.infer<typeof QuestionHandlingSchema>;

export const OBJECTION_HANDLINGS = ['good', 'partial', 'poor', 'unaddressed'] as const;
export const ObjectionHandlingSchema = z.enum(OBJECTION_HANDLINGS);

/**
 * Appointment states with provenance (decision 2). confirmed_external is reserved for HubSpot/calendar data;
 * an AI reading of the chat can at most reach confirmed_from_conversation, always with verified = false.
 */
export const APPOINTMENT_STATES = ['none', 'opportunity', 'proposed', 'accepted', 'confirmed_from_conversation', 'confirmed_external'] as const;
export const AppointmentStateSchema = z.enum(APPOINTMENT_STATES);
export type AppointmentState = z.infer<typeof AppointmentStateSchema>;

export const NEXT_ACTION_TYPES = [
  'appointment_scheduled',
  'appointment_proposed',
  'follow_up_scheduled',
  'pending_advisor_info',
  'pending_prospect_info',
  'not_qualified',
  'lost',
  'nurturing',
  'none',
] as const;
export const NextActionTypeSchema = z.enum(NEXT_ACTION_TYPES);
export type NextActionType = z.infer<typeof NextActionTypeSchema>;

export const OUTCOME_CATEGORIES = ['appointment', 'follow_up', 'not_interested', 'no_budget', 'out_of_zone', 'no_response', 'future_purchase', 'lost', 'unresolved'] as const;
export const OutcomeCategorySchema = z.enum(OUTCOME_CATEGORIES);
export type OutcomeCategory = z.infer<typeof OutcomeCategorySchema>;

export const COMMUNICATION_ASPECTS = ['clarity', 'brevity', 'naturalness', 'personalization', 'spelling', 'professionalism'] as const;
export const CommunicationAspectSchema = z.enum(COMMUNICATION_ASPECTS);
export type CommunicationAspect = z.infer<typeof CommunicationAspectSchema>;

export const TOUCH_QUALITY_RATINGS = ['good', 'fair', 'poor'] as const;

export const FINDING_KINDS = [
  'intent',
  'intent_signal',
  'qualification',
  'discovery',
  'question_quality',
  'question',
  'objection',
  'appointment',
  'next_action',
  'outcome',
  'pending_action',
  'touch_quality',
  'communication',
  'materials',
  'project_match',
] as const;
export const FindingKindSchema = z.enum(FINDING_KINDS);
export type FindingKind = z.infer<typeof FindingKindSchema>;

// ───────── AI output (validated strictly; evidence IDs must exist in the input) ─────────

const Ids = z.array(z.string()).max(20);
const Short = (n: number) => z.string().max(n);
const Rating = z.number().int().min(0).max(4).nullable();

export const IntelligenceSectionSchema = z.strictObject({
  intentLevel: IntentLevelSchema,
  intentSignals: z.array(z.strictObject({ type: IntentSignalTypeSchema, evidenceIds: Ids })).max(20),
  qualification: z.array(z.strictObject({ slot: QualificationSlotSchema, value: Short(200), confidence: z.number().min(0).max(1), evidenceIds: Ids })).max(12),
  enoughContext: z.strictObject({ value: z.boolean().nullable(), rationale: Short(400) }),
  questionQuality: z.strictObject({
    applicable: z.boolean(),
    rating: Rating,
    interrogation: z.boolean(),
    repeatedQuestions: z.array(z.strictObject({ questionId: z.string(), alreadyAnsweredId: z.string() })).max(10),
    rationale: Short(400),
  }),
  questions: z
    .array(z.strictObject({ askedId: z.string(), summary: Short(200), topic: QuestionTopicSchema, handling: QuestionHandlingSchema, answerIds: Ids }))
    .max(20),
  objections: z
    .array(
      z.strictObject({
        messageId: z.string(),
        type: ObjectionTypeSchema,
        summary: Short(200),
        acknowledged: z.boolean(),
        specific: z.boolean(),
        evasive: z.boolean(),
        confrontational: z.boolean(),
        usefulQuestion: z.boolean(),
        keptProgress: z.boolean(),
        handling: ObjectionHandlingSchema,
        responseIds: Ids,
      }),
    )
    .max(10),
  appointment: z.strictObject({
    opportunity: z.strictObject({ detected: z.boolean(), evidenceIds: Ids }),
    proposed: z.strictObject({ value: z.boolean(), concreteTimes: z.boolean(), evidenceIds: Ids }),
    accepted: z.strictObject({ value: z.boolean(), evidenceIds: Ids }),
    confirmed: z.strictObject({ value: z.boolean(), scheduledFor: Short(80).nullable(), evidenceIds: Ids }),
  }),
  nextAction: z.strictObject({ type: NextActionTypeSchema, description: Short(300).nullable(), dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(), evidenceIds: Ids }),
  outcome: z.strictObject({ category: OutcomeCategorySchema, rationale: Short(300), evidenceIds: Ids }),
  pendingActions: z
    .array(z.strictObject({ owner: z.enum(['advisor', 'prospect']), description: Short(200), severity: SeveritySchema, sinceId: z.string() }))
    .max(10),
  touchQuality: z
    .array(
      z.strictObject({
        touchId: z.string(),
        usesContext: z.boolean(),
        personalized: z.boolean(),
        newReason: z.boolean(),
        clearCta: z.boolean(),
        repetitive: z.boolean(),
        rating: z.enum(TOUCH_QUALITY_RATINGS),
        rationale: Short(300),
      }),
    )
    .max(20),
  communication: z
    .array(z.strictObject({ aspect: CommunicationAspectSchema, applicable: z.boolean(), rating: Rating, rationale: Short(300), evidenceIds: Ids }))
    .length(COMMUNICATION_ASPECTS.length),
  materials: z.strictObject({ applicable: z.boolean(), appropriate: z.boolean().nullable(), rationale: Short(300), evidenceIds: Ids }),
  projectMatch: z.strictObject({ projectId: z.string(), confidence: z.number().min(0).max(1) }).nullable(),
});
export type IntelligenceSection = z.infer<typeof IntelligenceSectionSchema>;

/** Full evaluation output v2: legacy analysis fields (kept for reports) + Conversation Intelligence. */
export const EvaluationOutputSchema = z.strictObject({
  primaryMotive: PrimaryMotiveSchema,
  intent: IntentSchema,
  summary: z.string().min(1).max(1200),
  productInterest: z.string().max(300).nullable(),
  dimensions: z.array(DimensionEvaluationSchema.strict()).length(5),
  strengths: z.array(z.string().max(300)).max(6),
  coaching: z.array(z.string().max(300)).max(6),
  evidenceMessageIds: z.array(z.string()).max(60),
  uncertaintyFlags: z.array(z.string().max(80)).max(10),
  confidence: z.number().min(0).max(1).nullable(),
  intelligence: IntelligenceSectionSchema,
});
export type EvaluationOutput = z.infer<typeof EvaluationOutputSchema>;

/** Derived appointment state (highest reached). AI never yields confirmed_external. */
export function appointmentStateOf(a: IntelligenceSection['appointment']): AppointmentState {
  if (a.confirmed.value) return 'confirmed_from_conversation';
  if (a.accepted.value) return 'accepted';
  if (a.proposed.value) return 'proposed';
  if (a.opportunity.detected) return 'opportunity';
  return 'none';
}

// ───────── Wire format ─────────

export const IntelligenceFindingSchema = z.object({
  id: z.string(),
  /** Stable across versions, e.g. "question:<msgId>", "objection:<msgId>", "appointment". */
  findingKey: z.string(),
  kind: FindingKindSchema,
  data: z.record(z.string(), z.unknown()),
  evidenceMessageIds: z.array(z.string()),
  confidence: z.number().nullable(),
  severity: SeveritySchema.nullable(),
  /** ai = inferred from the conversation; external = verified system of record (future CRM/calendar). */
  source: z.enum(['ai', 'external']),
  verified: z.boolean(),
  observedAt: z.string().nullable(),
});
export type IntelligenceFinding = z.infer<typeof IntelligenceFindingSchema>;

export const ConversationIntelligenceSchema = z.object({
  id: z.string(),
  episodeId: z.string(),
  analysisId: z.string(),
  isCurrent: z.boolean(),
  isProvisional: z.boolean(),
  schemaVersion: z.string(),
  promptVersion: z.string(),
  sourceRevision: z.number().int(),
  intentLevel: IntentLevelSchema,
  appointmentState: AppointmentStateSchema,
  nextActionType: NextActionTypeSchema,
  outcome: OutcomeCategorySchema,
  enoughContext: z.boolean().nullable(),
  project: z.object({ id: z.string(), version: z.number().int() }).nullable(),
  findings: z.array(IntelligenceFindingSchema),
  createdAt: z.string(),
});
export type ConversationIntelligence = z.infer<typeof ConversationIntelligenceSchema>;

// ───────── Labels ─────────

export const INTENT_SIGNAL_LABELS: Record<IntentSignalType, string> = {
  asks_price: 'Pregunta precio',
  asks_availability: 'Pregunta disponibilidad',
  asks_payment_terms: 'Pregunta formas de pago',
  asks_delivery_date: 'Pregunta fecha de entrega',
  asks_location: 'Pregunta ubicación',
  wants_visit: 'Quiere visitar',
  compares_units: 'Compara unidades',
  asks_discount: 'Pregunta descuentos',
  shares_budget: 'Comparte presupuesto',
  mentions_purchase_date: 'Menciona fecha de compra',
  asks_how_to_reserve: 'Pregunta cómo apartar',
  requests_information: 'Pide información',
  asks_questions: 'Hace preguntas',
  explores_alternatives: 'Explora alternativas',
  minimal_responses: 'Respuestas mínimas',
  no_timing: 'Sin timing',
  only_downloaded_information: 'Solo descargó información',
};

export const QUALIFICATION_SLOT_LABELS: Record<QualificationSlot, string> = {
  purpose: 'Vivir o invertir',
  budget: 'Presupuesto',
  payment_method: 'Forma de pago',
  zone: 'Zona',
  property_type: 'Tipo de propiedad',
  bedrooms: 'Recámaras',
  timing: 'Timing',
  motive: 'Motivo de compra',
  comparing: 'Qué está comparando',
  main_objection: 'Objeción principal',
};

export const QUESTION_TOPIC_LABELS: Record<QuestionTopic, string> = {
  price: 'Precio',
  availability: 'Disponibilidad',
  location: 'Ubicación',
  financing: 'Financiamiento',
  delivery: 'Entrega',
  maintenance: 'Mantenimiento',
  amenities: 'Amenidades',
  process: 'Proceso de compra',
  other: 'Otro',
};

export const QUESTION_HANDLING_LABELS: Record<QuestionHandling, string> = {
  answered: 'Respondida',
  partial: 'Respondida parcialmente',
  evaded: 'Evadida',
  ignored: 'Ignorada',
  changed_topic: 'Cambió de tema',
  pending: 'Pendiente de respuesta',
};

export const APPOINTMENT_STATE_LABELS: Record<AppointmentState, string> = {
  none: 'Sin cita',
  opportunity: 'Oportunidad para pedir cita',
  proposed: 'Cita propuesta',
  accepted: 'Cita aceptada',
  confirmed_from_conversation: 'Cita confirmada en la conversación (no verificada)',
  confirmed_external: 'Cita confirmada (verificada)',
};

export const NEXT_ACTION_LABELS: Record<NextActionType, string> = {
  appointment_scheduled: 'Cita agendada',
  appointment_proposed: 'Cita propuesta',
  follow_up_scheduled: 'Seguimiento programado',
  pending_advisor_info: 'Información pendiente del asesor',
  pending_prospect_info: 'Información pendiente del prospecto',
  not_qualified: 'Lead no calificado',
  lost: 'Lead perdido',
  nurturing: 'Lead en nurturing',
  none: 'Sin siguiente acción',
};

export const OUTCOME_LABELS: Record<OutcomeCategory, string> = {
  appointment: 'Cita',
  follow_up: 'Seguimiento',
  not_interested: 'No interesado',
  no_budget: 'Sin presupuesto',
  out_of_zone: 'Fuera de zona',
  no_response: 'No respondió',
  future_purchase: 'Compra futura',
  lost: 'Perdido',
  unresolved: 'Sin resolución',
};

export const COMMUNICATION_ASPECT_LABELS: Record<CommunicationAspect, string> = {
  clarity: 'Claridad',
  brevity: 'Brevedad',
  naturalness: 'Naturalidad',
  personalization: 'Personalización',
  spelling: 'Ortografía',
  professionalism: 'Profesionalismo',
};

export const FINDING_KIND_LABELS: Record<FindingKind, string> = {
  intent: 'Intención',
  intent_signal: 'Señal de intención',
  qualification: 'Calificación',
  discovery: 'Contexto suficiente',
  question_quality: 'Calidad de preguntas',
  question: 'Pregunta del prospecto',
  objection: 'Objeción',
  appointment: 'Cita',
  next_action: 'Siguiente acción',
  outcome: 'Resultado',
  pending_action: 'Acción pendiente',
  touch_quality: 'Calidad del seguimiento',
  communication: 'Comunicación',
  materials: 'Materiales',
  project_match: 'Proyecto sugerido',
};

/** Intent level weight lookup helper (policy weights are keyed by level). */
export function intentWeight(weights: Record<IntentLevel, number>, level: IntentLevel): number {
  return weights[level] ?? 1;
}
